// openMessageEditor.js
'use strict';
import { v4 } from 'uuid';

import { MERGE_OPERATION_DATA } from '../ActionTypes.js';
import {
  FEATURE_EXPIRE_EDIT_POST,
  FEATURE_EDIT_POST_PRICE,
  SHORT_VIDEO_DURATION_SECONDS,
} from '../RemoteConfigKeys.js';

import getMessageData from '../selector/getMessageData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getAssetData from '../selector/getAssetData.js';

import toggleModal from '../action/toggleModal.js';
import fetchMessageDetail from '../action/fetchMessageDetail.js';

import { PostDraftField, UploadJobType } from '../resource/uploadConstants.js';
import { Purpose as AssetPurpose } from '../resource/assetConstants.js';

/**
 * Open message editor
 * @kind action
 * @param {string} {messageId} - message id.
 * @return {Promise} Action promise.
 */
const openMessageEditor =
  ({ messageId } = {}) =>
  async (dispatch, getState) => {
    await dispatch(fetchMessageDetail({ id: messageId, forceRefetch: true }));
    const state = getState();
    const hasTtl = getMessageData(state, messageId, 'ttl') != null;
    const scheduledAtUnix = getMessageData(state, messageId, 'scheduledAtUnix');
    const isPublished = getMessageData(state, messageId, 'isPublished');
    const categories = getMessageData(state, messageId, 'categories');
    const isEditTtlEnabled =
      getRemoteConfigData(state, FEATURE_EXPIRE_EDIT_POST) === 1;
    const assetIds = getMessageData(state, messageId, 'assetIds') || [];
    const firstAssetId = assetIds[0];
    const duration = getAssetData(state, [firstAssetId], 'duration');
    const shortVideoMaxSeconds = getRemoteConfigData(
      state,
      SHORT_VIDEO_DURATION_SECONDS
    );

    // only video post need title and only one asset
    const shouldShowTitle =
      duration >= shortVideoMaxSeconds && assetIds.length === 1;

    dispatch({
      type: MERGE_OPERATION_DATA,
      payload: {
        selectPath: ['postDraft'],
        data: {
          [PostDraftField.ENTRY]: {
            value: hasTtl ? UploadJobType.STORY : UploadJobType.POST,
          },
          [PostDraftField.UPLOAD_JOB_ID]: {
            value: v4(),
          },
          [AssetPurpose.POST]: {
            assetIds: assetIds,
          },
          [PostDraftField.CAPTION]: {
            value:
              getMessageData(state, messageId, 'originalCaptionText') || '',
          },
          [PostDraftField.TITLE]: {
            value: shouldShowTitle
              ? getMessageData(state, messageId, 'originalTitle') || ''
              : '',
          },
          [PostDraftField.IS_AUP_CHECKED]: {
            value: true,
          },
          [PostDraftField.IS_AUP_CHECKBOX_EDITABLE]: {
            value: false,
          },
          [PostDraftField.CATEGORIES]: {
            value: categories || [],
          },
          [PostDraftField.UNLOCK_PRICE]: {
            value: getMessageData(state, messageId, 'unlockPrice'),
          },
          [PostDraftField.IS_UNLOCK_PRICE_EDITABLE]: {
            value: isPublished
              ? getRemoteConfigData(state, FEATURE_EDIT_POST_PRICE) === 1
              : !isPublished,
          },
          [PostDraftField.IS_SCHEDULED_AT_UNIX_EDITABLE]: {
            value: !isPublished,
          },
          [PostDraftField.IS_SCHEDULE_ENABLED]: {
            value: !!scheduledAtUnix,
          },
          [PostDraftField.SCHEDULED_AT_UNIX]: {
            value: scheduledAtUnix,
          },
          [PostDraftField.IS_TTL_CHECKED]: {
            value: hasTtl,
          },
          [PostDraftField.IS_TTL_EDITABLE]: {
            value: !isPublished && isEditTtlEnabled,
          },
          [PostDraftField.MESSAGE_ID]: {
            value: messageId,
          },
          [PostDraftField.IS_EDIT_MODE]: {
            value: true,
          },
        },
      },
    });
    return dispatch(
      toggleModal({
        modalId: 'PostDraft',
        isOpen: true,
      })
    );
  };

export default openMessageEditor;
